import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { bekleyenSayisi, kuyruguIsle } from './outbox';
import { onbellekDinle, onbellektenMi } from './api';

/**
 * SENKRONİZASYON — ön planda, görünür, platformdan bağımsız.
 *
 * Tetikleyiciler (Background Sync API YOK — iOS'ta desteklenmiyor, ona bel
 * bağlamak iOS'ta sessiz bozulma demek olurdu):
 *   1. Uygulama açıldığında
 *   2. Sekme ÖNE GELDİĞİNDE (telefonda uygulamaya dönüş)
 *   3. Bağlantı geri geldiğinde (`online` olayı)
 *   4. Uygulama açıkken periyodik (30 sn)
 */

interface SenkronDurumu {
  cevrimici: boolean;
  bekleyen: { komut: number; foto: number };
  senkronda: boolean;
  simdiSenkronla: () => Promise<void>;
  tazele: () => Promise<void>;
}

const Ctx = createContext<SenkronDurumu | null>(null);

export function SenkronSaglayici({ children }: { children: ReactNode }) {
  const [cevrimici, setCevrimici] = useState(navigator.onLine);
  const [bekleyen, setBekleyen] = useState({ komut: 0, foto: 0 });
  const [senkronda, setSenkronda] = useState(false);

  const tazele = useCallback(async () => {
    setBekleyen(await bekleyenSayisi());
  }, []);

  const simdiSenkronla = useCallback(async () => {
    if (senkronda || !navigator.onLine) return;
    setSenkronda(true);
    try {
      await kuyruguIsle();
      await tazele();
    } finally {
      setSenkronda(false);
    }
  }, [senkronda, tazele]);

  useEffect(() => {
    void tazele();

    const online = () => {
      setCevrimici(true);
      void simdiSenkronla();
    };
    const offline = () => setCevrimici(false);
    // Telefonda uygulamaya dönünce — iOS'ta arka plan çalışmadığı için kritik.
    const gorunurluk = () => {
      if (document.visibilityState === 'visible') void simdiSenkronla();
    };

    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', gorunurluk);
    const t = setInterval(() => void simdiSenkronla(), 30_000);

    void simdiSenkronla();

    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', gorunurluk);
      clearInterval(t);
    };
    // simdiSenkronla her renderda degisiyor; sabit referans icin bilinçli olarak dışarıda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Ctx.Provider value={{ cevrimici, bekleyen, senkronda, simdiSenkronla, tazele }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSenkron(): SenkronDurumu {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSenkron, SenkronSaglayici içinde kullanılmalı');
  return c;
}

/**
 * KALICI GÖSTERGE — "3 fotoğraf yüklenmeyi bekliyor".
 *
 * Bu gösterge, Background Sync'e bel bağlamamanın karşılığıdır: kullanıcı
 * neyin gitmediğini HER ZAMAN görür. Sessiz kayıp yok.
 */
export function SenkronCubugu() {
  const { cevrimici, bekleyen, senkronda, simdiSenkronla } = useSenkron();
  const [onbellek, setOnbellek] = useState(onbellektenMi());
  const toplam = bekleyen.komut + bekleyen.foto;

  useEffect(() => onbellekDinle(setOnbellek), []);

  /*
   * ÇEVRİMDIŞI OKUMA BANDI — service worker son bilinen cevabı döndürdüğünde.
   * Ekranda veri VAR ama TAZE DEĞİL; bunu söylememek, sahadaki kişinin eski bir
   * listeye bakıp "bu iş bana atanmamış" sanmasına yol açar.
   */
  if (onbellek && toplam === 0) {
    return (
      <div className="sticky top-0 z-40 flex items-center gap-2 bg-wait px-4 py-2 text-sm text-white">
        <span className="flex-1">
          <strong>Çevrimdışı</strong> · son bilinen durum gösteriliyor
        </span>
        {cevrimici && (
          <button onClick={() => window.location.reload()} className="underline">
            yenile
          </button>
        )}
      </div>
    );
  }

  if (cevrimici && toplam === 0) return null;

  const parcalar: string[] = [];
  if (bekleyen.foto) parcalar.push(`${bekleyen.foto} fotoğraf`);
  if (bekleyen.komut) parcalar.push(`${bekleyen.komut} işlem`);

  return (
    <div
      className={`sticky top-0 z-40 flex items-center gap-3 px-4 py-2 text-sm text-white ${
        cevrimici ? 'bg-stok' : 'bg-wait'
      }`}
    >
      <span className="flex-1">
        {!cevrimici && <strong>Çevrimdışı</strong>}
        {!cevrimici && toplam > 0 && ' · '}
        {toplam > 0 && (
          <>
            {parcalar.join(' ve ')} yüklenmeyi bekliyor
            {!cevrimici && ' — bağlantı gelince gönderilecek'}
          </>
        )}
        {cevrimici && toplam > 0 && senkronda && ' · gönderiliyor…'}
      </span>
      {cevrimici && toplam > 0 && !senkronda && (
        <button onClick={() => void simdiSenkronla()} className="underline">
          şimdi gönder
        </button>
      )}
    </div>
  );
}
